import { List } from '@mairistem/composants';
import _ from 'lodash';
import React from 'react';

import { ENCAISSEMENT } from '@/constants';
import type { DataReglementType } from '@/types';
import ModalEncaissementReglement from './ModalEncaissementReglement';

type ReglementWithAnimation = DataReglementType & {
  isNew?: boolean;
  isRemoving?: boolean;
};

// Hook pour gérer les animations d'apparition et de suppression
const useAnimatedItems = (items: DataReglementType[]) => {
  const [animatedItems, setAnimatedItems] = React.useState<ReglementWithAnimation[]>([]);
  // Stocker une copie complète des items précédents (pas juste les IDs)
  const prevItemsRef = React.useRef<Map<string | number, DataReglementType>>(new Map());
  const isFirstRender = React.useRef(true);

  React.useEffect(() => {
    // Gérer le cas où items est undefined ou null
    const safeItems = items ?? [];

    const currentIds = new Set(_.map(safeItems, 'identifiant'));
    const currentItemsMap = new Map(safeItems.map((item) => [item.identifiant, item]));

    // Premier rendu : initialiser sans animation
    if (isFirstRender.current) {
      isFirstRender.current = false;
      prevItemsRef.current = currentItemsMap;
      setAnimatedItems(safeItems.map((item) => ({ ...item })));
      return;
    }

    // Trouver les nouveaux IDs
    const newIds = new Set<string | number>();
    currentIds.forEach((id) => {
      if (!prevItemsRef.current.has(id)) {
        newIds.add(id);
      }
    });

    // Trouver les IDs supprimés et récupérer leurs données complètes
    const removedItems: DataReglementType[] = [];
    prevItemsRef.current.forEach((item, id) => {
      if (!currentIds.has(id)) {
        removedItems.push(item);
      }
    });

    // Construire la nouvelle liste avec les animations
    setAnimatedItems((prev) => {
      // Conserver les items en cours de suppression de l'état précédent
      const prevRemovingItems = prev.filter((p) => p.isRemoving);

      // Ajouter les nouveaux éléments avec le flag isNew
      const updatedItems: ReglementWithAnimation[] = safeItems.map((item) => {
        // Conserver le flag isNew de l'état précédent s'il existe
        // Cela évite de retirer la classe 'new' en cas de re-render pendant l'animation
        const prevItem = prev.find((p) => p.identifiant === item.identifiant);
        return {
          ...item,
          isNew: prevItem?.isNew || newIds.has(item.identifiant),
          isRemoving: false,
        };
      });

      // Créer les éléments en suppression avec leurs données complètes d'origine
      const newRemovingItems: ReglementWithAnimation[] = removedItems.map((item) => ({
        ...item,
        isRemoving: true,
        isNew: false,
      }));

      // Combiner les items déjà en cours de suppression et les nouveaux
      // Éviter les doublons en vérifiant l'identifiant
      const allRemovingItems = [
        ...prevRemovingItems,
        ...newRemovingItems.filter(
          (newItem) => !prevRemovingItems.some((p) => p.identifiant === newItem.identifiant),
        ),
      ];

      // Insérer les éléments en cours de suppression à leur ancienne position
      const result = [...updatedItems];
      allRemovingItems.forEach((removingItem) => {
        const oldIndex = prev.findIndex((p) => p.identifiant === removingItem.identifiant);
        if (oldIndex !== -1) {
          result.splice(Math.min(oldIndex, result.length), 0, removingItem);
        }
      });

      return result;
    });

    // Retirer les éléments supprimés après l'animation
    if (removedItems.length > 0) {
      setTimeout(() => {
        setAnimatedItems((prev) => prev.filter((item) => !item.isRemoving));
      }, 300);
    }

    // Retirer le flag isNew après l'animation pour les nouveaux items
    if (newIds.size > 0) {
      setTimeout(() => {
        setAnimatedItems((prev) =>
          prev.map((item) => (newIds.has(item.identifiant) ? { ...item, isNew: false } : item)),
        );
      }, 300);
    }

    // Mettre à jour la référence avec les items actuels
    prevItemsRef.current = currentItemsMap;
  }, [items]);

  return animatedItems;
};

export interface ModalEncaissementReglementsProps {
  addButtonVisible?: boolean;
  animated?: boolean;
  looc?: boolean;
  getModesReglementsDesactives?: (reglement: DataReglementType) => string[];
  readOnly?: boolean;
  reglementsComplet?: boolean;
  payeur?: string;
  reste?: number;
  reglements: DataReglementType[];
  onReglementChange?: (
    indice: number,
  ) => (data: DataReglementType | null, complet: boolean) => void;
  onReglementReset?: (indice: number) => (identifiantModeReglement: number) => void;
  onReglementMultipleDelete?: (indice: number) => void;
  children?: React.ReactNode;
}

const ModalEncaissementReglements = ({
  addButtonVisible = true,
  animated = true,
  readOnly = false,
  reglements,
  payeur = '',
  reste = 0,
  reglementsComplet = false,
  looc = false,
  onReglementChange,
  onReglementMultipleDelete,
  onReglementReset,
  getModesReglementsDesactives,
  children,
}: ModalEncaissementReglementsProps) => {
  const animatedItems = useAnimatedItems(reglements);

  // Utiliser les items animés ou les règlements bruts selon la prop
  const displayItems = animated ? animatedItems : reglements;

  // Fonction pour obtenir le className de l'item (seulement si animated)
  const getItemClassName = (item: ReglementWithAnimation) => {
    if (!animated) return undefined;
    if (item.isRemoving) return 'removing';
    if (item.isNew) return 'new';
    return undefined;
  };

  return (
    <List
      className="domain-colored"
      style={{ maxHeight: 256, overflowY: 'auto', marginBottom: 0 }}
      // selection={!readOnly}
      celled
    >
      {_.map(displayItems, (reglement, index) => {
        // Trouver l'index réel dans reglements (pour les callbacks)
        const realIndex = animated
          ? _.findIndex(reglements, { identifiant: reglement.identifiant })
          : index;

        // Calcul du reste à payer pour chaque règlement
        // On prend le reste total - les montants des reglèments saisi
        let resteReglement = 0;
        _.forEach(reglements, (r, indice) => {
          if (indice !== realIndex) {
            resteReglement += r.montant;
          }
        });

        const isRemoving = animated && (reglement as ReglementWithAnimation).isRemoving;

        // Nettoyer les propriétés d'animation avant de passer au composant enfant
        const cleanReglement = _.omit(reglement, ['isNew', 'isRemoving']) as DataReglementType;

        return (
          <List.Item
            key={`reglement@${reglement.identifiant}`}
            className={
              (looc ? 'looc ' : '') + (getItemClassName(reglement as ReglementWithAnimation) || '')
            }
          >
            <ModalEncaissementReglement
              readOnly={readOnly}
              reglement={cleanReglement}
              // Modes de règlement à désactiver (déjà utilisés parmi ceux à restriction unique)
              reglementsDesactives={getModesReglementsDesactives?.(reglement) ?? []}
              indice={realIndex}
              reste={parseFloat((reste - resteReglement).toFixed(2))}
              payeur={payeur}
              {...(!readOnly &&
                !isRemoving &&
                realIndex !== -1 && {
                  onReglementChange: onReglementChange(realIndex),
                  onReglementReset: onReglementReset(realIndex),
                  disabledReglementMultiple: !reglementsComplet,
                  ...(_.size(reglements) > 1 && { onReglementMultipleDelete }),
                })}
              buttonAddReglement={
                addButtonVisible &&
                realIndex === 0 &&
                _.size(reglements) < ENCAISSEMENT.NOMBRE_REGLEMENTS_MAX
                  ? children
                  : null
              }
            />
          </List.Item>
        );
      })}
    </List>
  );
};

export default ModalEncaissementReglements;
